import { Button, Drawer, InfoCard, Input, P, Skeleton, Tag } from '@dnb/eufemia';
import { filter } from '@dnb/eufemia/icons';
import styled from '@emotion/styled';
import { deepCopy } from '@portals/shared/common/utils';
import { TrackRoute } from '@portals/shared-frontend/components';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import { HeroContentMenu } from '@/components/HeroPage/HeroContentMenu';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import { useBackend } from '@/hooks/useBackend';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { SERVICE_DOMAIN_KEY } from '@/pages/explorer/filters/ServiceDomainFilter';
import { STAGE_FILTER_KEY } from '@/pages/explorer/filters/StageFilter';
import { TAG_FILTER_KEY } from '@/pages/explorer/filters/TagFilter';
import { CLASSIFICATION_FILTER_KEY } from '@/pages/explorer/filters/TypeFilter';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { capitalizeFirstLetterOfString } from '@/utils/textUtils';

import McpFilters from './McpFilters';
import McpList from './McpList';
import { useMcpFilters } from './useMcpFilters';

const StyledDrawer = styled(Drawer)`
  background-color: var(--color-black-3);
`;

export default function McpExplorerPage() {
  const { featureFlags } = useFeatureFlags();
  const navigate = useNavigate();
  const { clearAllFilters, updateFilterInURL } = useUrlFilters();
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const { data: mcps, isLoading, mutate } = useBackend('/mcp');
  const { data: serviceDomains } = useBackend('/service-now/service-domains');
  const {
    filteredMcps,
    query,
    serviceDomainFilter,
    serviceDomainOptions,
    setQuery,
    stageFilter,
    tagFilter,
    tagOptions,
    typeFilter,
    typeOptions,
  } = useMcpFilters(mcps, serviceDomains);

  const searchField = useMemo(() => {
    return (
      <Input
        clear
        icon="loupe"
        on_change={({ value }) => {
          setQuery(value);
        }}
        placeholder="Search"
        stretch
        value={query}
      />
    );
  }, [query, setQuery]);

  const filters = useMemo(() => <McpFilters mcps={mcps} serviceDomains={serviceDomains} />, [mcps, serviceDomains]);

  const selectedType = typeFilter[0];
  const hasActiveFilters =
    typeFilter.length > 0 || stageFilter.length > 0 || serviceDomainFilter.length > 0 || tagFilter.length > 0;

  if (!featureFlags.ENABLE_MCP_PAGES) {
    return <NotFoundPage />;
  }

  return (
    <div className="dark-mode">
      <HeroPage banner="explorer" title="MCP Explorer">
        <TrackRoute name="MCP Explorer" path="/explorer/mcps" type="mcp" />
        <HeroMenu>
          {searchField}
          <Button
            className="self-start"
            onClick={() => {
              clearAllFilters();
              setQuery('');
            }}
            variant="tertiary"
          >
            Clear Filters
          </Button>
          {filters}
        </HeroMenu>
        <HeroContent>
          <InfoCard
            acceptButtonText="Add MCP"
            bottom="medium"
            onAccept={() => navigate('add')}
            text="Publish your MCP's guide so it's discoverable in the Developer Portal and in RAiCode, where anyone just asks `which MCP fits my use case?` and finds yours."
            title="Publish your MCP"
          />
          <HeroContentMenu>
            {searchField}
            <StyledDrawer
              id="mcpFilterDrawer"
              onClose={() => setFilterMenuOpen(false)}
              openState={filterMenuOpen}
              title="Filters"
              top="small"
              triggerAttributes={{
                icon: filter,
                text: 'Filters',
                variant: 'tertiary',
              }}
            >
              {filters}
            </StyledDrawer>
            {hasActiveFilters && (
              <div>
                <Tag.Group label="Filters" top="x-small">
                  {selectedType && selectedType !== 'all' && (
                    <Tag
                      onClick={() =>
                        updateFilterInURL(
                          CLASSIFICATION_FILTER_KEY,
                          [],
                          typeOptions.map((type) => ({
                            selectedKey: type,
                            content: [type],
                          })),
                        )
                      }
                      variant="removable"
                    >
                      {capitalizeFirstLetterOfString(selectedType)}
                    </Tag>
                  )}
                  {stageFilter.map((stage) => (
                    <Tag
                      key={`stage-${stage}`}
                      onClick={() =>
                        updateFilterInURL(
                          STAGE_FILTER_KEY,
                          stageFilter.filter((selectedStage) => selectedStage !== stage),
                          stageFilter.map((selectedStage) => ({
                            selectedKey: selectedStage,
                            content: [selectedStage],
                          })),
                        )
                      }
                      variant="removable"
                    >
                      {capitalizeFirstLetterOfString(stage)}
                    </Tag>
                  ))}
                  {serviceDomainFilter.map((serviceDomain) => (
                    <Tag
                      key={`serviceDomain-${serviceDomain}`}
                      onClick={() =>
                        updateFilterInURL(
                          SERVICE_DOMAIN_KEY,
                          serviceDomainFilter.filter(
                            (selectedServiceDomain) => selectedServiceDomain !== serviceDomain,
                          ),
                          serviceDomainOptions,
                        )
                      }
                      variant="removable"
                    >
                      {serviceDomainOptions.find((option) => option.selectedKey === serviceDomain)?.content[1]}
                    </Tag>
                  ))}
                  {tagFilter.map((tag) => (
                    <Tag
                      key={`tag-${tag}`}
                      onClick={() =>
                        updateFilterInURL(
                          TAG_FILTER_KEY,
                          tagFilter.filter((selectedTag) => selectedTag !== tag),
                          tagOptions,
                        )
                      }
                      variant="removable"
                    >
                      {tag}
                    </Tag>
                  ))}
                </Tag.Group>
              </div>
            )}
          </HeroContentMenu>
          <Skeleton show={isLoading}>
            <P bottom="medium" weight="medium">
              Showing {filteredMcps.length} of {(mcps ?? []).length} MCPs
            </P>
            {filteredMcps.length === 0 ? (
              <InfoCard text="No MCPs match the selected filters." />
            ) : (
              <McpList
                mcps={filteredMcps}
                onMcpFavouriteChange={(mcpId, isFavorite) =>
                  mutate((mcpList) => {
                    if (!mcpList) return mcpList;
                    const index = mcpList.findIndex((mcp) => mcp.id === mcpId);
                    if (index === -1) return mcpList;
                    const updatedMcps = deepCopy(mcpList);
                    updatedMcps[index]!.isFavorite = isFavorite;
                    return updatedMcps;
                  }, false)
                }
              />
            )}
          </Skeleton>
        </HeroContent>
      </HeroPage>
    </div>
  );
}
